<template>
  <div
    class="formcontrol__insurance"
    :class="[
      `type-${controlInsurance.type}`,
      {
        'is-on': isBoolean && controlInsurance.value,
        'is-off': isBoolean && !controlInsurance.value
      },
      {
        'is-clean': !controlValidator.$dirty,
        'is-dirty': controlValidator.$dirty
      },
      { 'is-valid': controlValidator.$dirty && !controlValidator.$invalid },
      { 'is-invalid': controlValidator.$dirty && controlValidator.$invalid }
    ]"
    style="position: relative;"
  >
    <div
      v-if="isBoolean"
      class="control__boolean"
      :aria-labelledby="`${controlInsurance.id}-label`"
      :aria-describedby="`${controlInsurance.id}-description`"
    >
      <label :id="`${controlInsurance.id}-label`">{{
        $t(controlInsurance.label)
      }}</label>

      <input
        :id="controlInsurance.id"
        type="hidden"
        v-model.trim="control.value"
      />

      <switcher
        :state="controlInsurance.value"
        :textOn="$t(controlInsurance.togglerOn)"
        :textOff="$t(controlInsurance.togglerOff)"
        :disabled="isDisabled"
        @switcherChange="updateControlValue"
        variant="white"
      />
    </div>

    <div v-else-if="isText">
      <div class="control__text control__pseudotitle">
        {{ $t(controlInsurance.placeholder) }}
      </div>
      <div
        class="control__text"
        :aria-labelledby="`${controlInsurance.id}-label`"
        :aria-describedby="`${controlInsurance.id}-description`"
      >
        <input
          :id="controlInsurance.id"
          class="control__input"
          type="number"
          :disabled="isDisabled || isDisabledByParent"
          v-model.trim="control.value"
        />

        <div class="control__hint">{{ $t(controlInsurance.hint) }}</div>
      </div>
    </div>

    <i18n
      v-if="$te(`${controlInsurance.description}.text`)"
      :id="`${controlInsurance.id}-description`"
      class="control__description"
      :path="`${controlInsurance.description}.text`"
      tag="div"
    >
      <br slot="br" />
      <font-awesome-icon
        v-if="$te(`${controlInsurance.description}.tooltip`)"
        slot="tooltip"
        icon="info-circle"
        v-tooltip="{ content: $t(`${controlInsurance.description}.tooltip`) }"
        class="tooltip-icon"
        :tabindex="isTooltipDisabled"
        size="lg"
      />
    </i18n>

    <i18n
      v-if="$te(`${controlInsurance.costs}.text`)"
      class="control__costs"
      :class="{ control__boldline: controlInsurance.boldline }"
      :path="`${controlInsurance.costs}.text`"
      tag="div"
    >
      <br slot="br" />
      <font-awesome-icon
        v-if="$te(`${controlInsurance.costs}.tooltip`)"
        slot="tooltip"
        icon="info-circle"
        v-tooltip="{ content: $t(`${controlInsurance.costs}.tooltip`) }"
        class="tooltip-icon"
        :tabindex="isTooltipDisabled"
        size="lg"
      />
    </i18n>

    <template v-if="control.dependants">
      <form-control-insurance
        v-for="(control, controlKey) in control.dependants"
        :key="controlKey"
        :validator="validator.dependants[controlKey]"
        :control="control"
      />
    </template>
  </div>
</template>

<script>
import Switcher from '@/components/Switcher'

export default {
  name: 'FormControlInsurance',
  props: {
    validator: {
      type: Object,
      required: true
    },
    control: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      controlValidator: (this.validator || {}).value,
      controlInsurance: this.control
    }
  },
  computed: {
    isBoolean() {
      return this.controlInsurance.type === 'boolean'
    },
    isText() {
      return this.controlInsurance.type === 'text'
    },
    isDisabledByParent() {
      return (
        this.$parent.$options.name === this.$options.name &&
        this.$parent.isBoolean &&
        this.$parent.controlInsurance.value === false
      )
    },
    isTooltipDisabled() {
      return (this.isBoolean && this.controlInsurance.value === false) ||
        this.isDisabledByParent
        ? -1
        : false
    },
    isDisabled() {
      return this.controlInsurance.disabled &&
        typeof this.controlInsurance.disabled === 'function'
        ? this.controlInsurance.disabled()
        : this.controlInsurance.disabled
    }
  },
  methods: {
    updateControlValue(value) {
      this.controlInsurance.value = value
      this.controlValidator.$touch()

      this.$emit('controlChange', {
        id: this.controlInsurance.id,
        type: this.controlInsurance.type,
        value: this.controlInsurance.value
      })
    }
  },
  watch: {
    '$props.control.value'(newValue) {
      if (newValue !== this.$props.control.value) {
        this.updateControlValue(newValue)
      }
    }
  },
  components: {
    Switcher
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

$formcontrol-insurance-gap: 3rem !default;
$formcontrol-insurance-border: 1px solid $brand-gray !default;
$formcontrol-insurance-valid-color: $brand-green !default;
$formcontrol-insurance-invalid-color: $brand-red !default;

$formboolean-insurance-off-overlay: rgba($brand-white, 0.5) !default;

$formcontrol-insurance-boolean-padding: 1rem !default;
$formcontrol-insurance-boolean-bg: $brand-primary !default;
$formcontrol-insurance-boolean-color: $brand-white !default;
$formcontrol-insurance-boolean-label-font-size: inherit !default;
$formcontrol-insurance-boolean-label-font-weight: $font-weight-bold !default;

$formcontrol-insurance-text-padding: 1rem 0 !default;
$formcontrol-insurance-text-bg: transparent !default;
$formcontrol-insurance-text-border: $formcontrol-insurance-border !default;
$formcontrol-insurance-text-color: inherit !default;
$formcontrol-insurance-text-input-base: 450px !default;
$formcontrol-insurance-text-input-gap: 1rem !default;
$formcontrol-insurance-text-input-padding: 0.2em 0.5em !default;
$formcontrol-insurance-text-input-border: 1px solid $brand-gray !default;
$formcontrol-insurance-text-input-disabled-bg: $brand-lightgray !default;
$formcontrol-insurance-text-input-disabled-border: $formcontrol-insurance-text-input-border !default;
$formcontrol-insurance-text-input-disabled-color: $brand-gray !default;
$formcontrol-insurance-text-hint-padding: 0.2em 0 !default;
$formcontrol-insurance-text-hint-font-size: 0.8em !default;
$formcontrol-insurance-text-hint-font-weight: inherit !default;

$formcontrol-insurance-description-padding: 1rem 0 !default;
$formcontrol-insurance-description-bg: transparent !default;
$formcontrol-insurance-description-border: $formcontrol-insurance-border !default;
$formcontrol-insurance-description-font-size: 1rem !default;
$formcontrol-insurance-description-font-weight: inherit !default;
$formcontrol-insurance-description-color: inherit !default;

$formcontrol-insurance-costs-padding: $formcontrol-insurance-description-padding !default;
$formcontrol-insurance-costs-bg: $formcontrol-insurance-description-bg !default;
$formcontrol-insurance-costs-border: $formcontrol-insurance-description-border !default;
$formcontrol-insurance-costs-font-size: $formcontrol-insurance-description-font-size !default;
$formcontrol-insurance-costs-font-weight: $font-weight-bold !default;
$formcontrol-insurance-costs-color: $formcontrol-insurance-description-color !default;

$formcontrol-insurance-tooltip-icon-color: $brand-blue !default;
$formcontrol-insurance-tooltip-icon-hover-color: $brand-blue !default;

$formaction-insurance-margin: 2rem 0 0 !default;

.formcontrol__insurance {
  + .formcontrol__insurance {
    margin-top: $formcontrol-insurance-gap;
  }

  .control__boolean {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: $formcontrol-insurance-boolean-padding;
    background-color: $formcontrol-insurance-boolean-bg;
    color: $formcontrol-insurance-boolean-color;
    border-radius: 10px;

    label {
      font-size: $formcontrol-insurance-boolean-label-font-size;
      font-weight: $formcontrol-insurance-boolean-label-font-weight;
    }
  }

  .control__text {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    padding: $formcontrol-insurance-text-padding;
    background-color: $formcontrol-insurance-text-bg;
    border-bottom: $formcontrol-insurance-text-border;
    color: $formcontrol-insurance-text-color;

    .control__input {
      flex: 0 1 $formcontrol-insurance-text-input-base;
      margin-right: $formcontrol-insurance-text-input-gap;
      padding: $formcontrol-insurance-text-input-padding;
      border: $formcontrol-insurance-text-input-border;
      font-family: inherit;
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
      outline: 0 none;

      &[disabled] {
        background-color: $formcontrol-insurance-text-input-disabled-bg;
        border: $formcontrol-insurance-text-input-disabled-border;
        color: $formcontrol-insurance-text-input-disabled-color;
      }
    }

    .control__hint {
      padding: $formcontrol-insurance-text-hint-padding;
      font-size: $formcontrol-insurance-text-hint-font-size;
      font-weight: $formcontrol-insurance-text-hint-font-weight;
    }
  }

  .control__pseudotitle {
    border-bottom: none;
    padding-bottom: 0;
    font-weight: bold;
  }

  .control__boldline {
    border-bottom-width: 2px !important;
  }

  .control__description {
    padding: $formcontrol-insurance-description-padding;
    background-color: $formcontrol-insurance-description-bg;
    border-bottom: $formcontrol-insurance-description-border;
    font-size: $formcontrol-insurance-description-font-size;
    font-weight: $formcontrol-insurance-description-font-weight;
    color: $formcontrol-insurance-description-color;
  }

  .control__costs {
    padding: $formcontrol-insurance-costs-padding;
    background-color: $formcontrol-insurance-costs-bg;
    border-bottom: $formcontrol-insurance-costs-border;
    font-size: $formcontrol-insurance-costs-font-size;
    font-weight: $formcontrol-insurance-costs-font-weight;
    color: $formcontrol-insurance-costs-color;
  }

  .tooltip-icon {
    color: $brand-primary;

    &:hover {
      color: $brand-primary;
    }
  }

  &:first-child {
    border-top: $formcontrol-insurance-border;
  }

  &.type-boolean {
    &.is-off {
      > div:not(.control__boolean) {
        opacity: 0.5;
        pointer-events: none;

      }
      > div.control__boolean {
        background-color: $brand-secondary;
      }
    }
  }

  &.type-text {
  }

  &.extends-before {
    margin-top: 0 !important;
  }

  .control__text {
    .control__input {
      max-width: 12rem;
      border-radius: 8px;
    }
  }

  .control__text {
    .control__input {
      border-color: $brand-primary;
      // border-color: $formcontrol-insurance-valid-color;
    }
  }
  &.type-boolean {
    &.is-off {
      .control__text {
        .control__input {
          border-color: $brand-secondary;
        }
      }
    }
  }

  &.is-valid {
    .control__text {
      .control__input {
        border-color: $brand-primary;
        // border-color: $formcontrol-insurance-valid-color;
      }
    }
    &.type-boolean {
      &.is-off {
        .control__text {
          .control__input {
            border-color: $brand-secondary;
          }
        }
      }
    }
  }

  &.is-invalid {
    .control__text {
      .control__input {
        border-color: $formcontrol-insurance-invalid-color;
      }

      .control__hint {
        color: $formcontrol-insurance-invalid-color;
      }
    }
  }

  &.is-off {
    &.is-valid,
    &.is-invalid {
      .control__text {
        .control__input {
          border: $formcontrol-insurance-text-input-border;
        }
      }
    }
  }
}
</style>
